import { useQuery } from '@tanstack/react-query';
import type { Location } from '@court-sync/shared';
import { publicClubService, type PublicClubProfile } from '@/services/publicClub';
import { STALE_TIME } from '@/queryClient';
import { unwrapApiDataOrEmpty } from './unwrap';

const publicClubKeys = {
  all: ['publicClub'] as const,
  profileBySlug: (slug: string) => [...publicClubKeys.all, 'profile', 'slug', slug] as const,
  profileBySubdomain: (label: string) =>
    [...publicClubKeys.all, 'profile', 'subdomain', label] as const,
  locations: (slug: string) => [...publicClubKeys.all, 'locations', slug] as const,
};

export interface UsePublicClubProfileOptions {
  slug?: string;
  subdomainLabel?: string;
  seed?: PublicClubProfile | null;
  hostLoading?: boolean;
  enabled?: boolean;
}

export function usePublicClubProfile(options: UsePublicClubProfileOptions) {
  const { slug, subdomainLabel, seed, hostLoading = false, enabled = true } = options;

  const queryKey = subdomainLabel
    ? publicClubKeys.profileBySubdomain(subdomainLabel)
    : slug
      ? publicClubKeys.profileBySlug(slug)
      : null;

  const canFetch = enabled && (subdomainLabel ? !hostLoading || !!seed : !!slug);

  const {
    data: profile = null,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: queryKey ?? ['publicClub', 'disabled'],
    queryFn: async () => {
      const res = subdomainLabel
        ? await publicClubService.getProfileBySubdomain(subdomainLabel)
        : await publicClubService.getProfile(slug!);
      if (!res.success || !res.data) {
        throw new Error(res.error || 'Club not found');
      }
      return res.data;
    },
    enabled: canFetch && !!queryKey,
    staleTime: STALE_TIME.MEDIUM,
    initialData: seed ?? undefined,
  });

  return {
    profile: profile ?? seed ?? null,
    isLoading: isLoading || (subdomainLabel ? hostLoading && !seed : false),
    error: error instanceof Error ? error.message : error ? String(error) : null,
    refetch,
    isFetching,
  };
}

export function usePublicClubLocations(slug: string | undefined, enabled = true) {
  const { data: locations = [], isLoading, error } = useQuery({
    queryKey: slug ? publicClubKeys.locations(slug) : ['publicClub', 'locations', 'disabled'],
    queryFn: async () => {
      const res = await publicClubService.getLocations(slug!);
      return unwrapApiDataOrEmpty(res);
    },
    enabled: enabled && !!slug,
    staleTime: STALE_TIME.MEDIUM,
  });

  return {
    locations: locations as Location[],
    isLoading,
    error: error instanceof Error ? error.message : error ? String(error) : null,
  };
}
